/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useTranslation } from 'react-i18next'

import { add, subtract } from '../../../utils/money'
import { ChargeDescription } from '../../invoices'
import {
  applyPromoCodeToQuote,
  applyPromoCodeToQuoteJob,
  type PromoCode,
} from '../../promoCodes'
import { type Quote } from './models/quote.model'
import { type QuoteJob } from './models/subModels/quoteJob.model'
import { type Amount } from '../../../common/amount'
import * as utils from './resolved.utils'

type GetPriceOptions = {
  promoCode?: PromoCode
}

export const useGetQuoteJobDate = () => {
  return (quoteJob: QuoteJob) => {
    return utils.getQuoteJobDate(quoteJob)
  }
}

/**
 * return quote job price information
 */
export const useGetQuoteJobPrice = () => (quote: Quote, jobIndex: number, {
  promoCode,
}: GetPriceOptions = {}) => {
  const job = quote.jobs[jobIndex]
  if (!job) {
    throw Error('Invalid job index')
  }

  const originalPrice = job.subtotal
  const price = applyPromoCodeToQuoteJob(quote, jobIndex, promoCode)
  const discount = promoCode ? subtract(price, originalPrice) : undefined

  return {
    price,
    originalPrice,
    discount: discount?.price !== 0 ? discount : undefined,
  }
}

/**
 * return quote price information
 */
export const useGetQuotePrice = () => (quote: Quote, {
  promoCode,
}: GetPriceOptions = {}) => {
  const originalPrice = quote.subtotal
  const price = applyPromoCodeToQuote(quote, promoCode)
  const discount = promoCode ? subtract(price, originalPrice) : undefined

  return {
    price,
    originalPrice,
    discount: discount?.price !== 0 ? discount : undefined,
  }
}

/**
 * check all alternative dates and pick the cheapest option for each jobs
 */
export const getQuoteCheapestPrice = (quote: Quote, {
  promoCode,
}: GetPriceOptions = {}) => {
  if (!quote.jobs[0].alternativeDates?.length) {
    return
  }
  const cheapestQuote = structuredClone(quote)
  let totalPrice: Amount | undefined
  cheapestQuote.jobs.forEach(job => {
    if (!job.alternativeDates) {
      return
    }
    const cheapest = {
      price: Math.min(
        ...job.alternativeDates.map(alternativeDate => alternativeDate.subtotal.price),
      ),
      currency: job.subtotal.currency,
    }
    totalPrice = totalPrice ? add(totalPrice, cheapest) : cheapest
    job.subtotal = cheapest
  })
  cheapestQuote.subtotal = totalPrice!
  return applyPromoCodeToQuote(cheapestQuote, promoCode)
}

/**
 * check all alternative dates and pick the cheapest option for each jobs
 */
export const useGetQuoteCheapestPrice = () => getQuoteCheapestPrice

export const useGetQuoteJobLabel = () => {
  const { t } = useTranslation()
  return (quoteJob: QuoteJob) => {
    const hasMoving = quoteJob.charges.some(charge => charge.description === ChargeDescription.movingLabour)
    const hasPacking = quoteJob.charges.some(charge => charge.description === ChargeDescription.packingLabour)

    const actions = []
    if (hasMoving) {
      actions.push(t('quotes.quote.jobDescription.moving'))
    }
    if (hasPacking) {
      actions.push(t('quotes.quote.jobDescription.packing'))
    }
    return actions.join(' + ')
  }
}

/**
 * loop all jobs to gather the nb men and trucks in the quote
 * if 2 jobs have different values, returns nothing
 */
export const useGetQuoteMenAndTrucks = () => {
  return (quote: Quote, jobId?: string) => {
    if (jobId) {
      const jobQuote = quote.jobs.find((jobQuote) => jobQuote.jobId === jobId)
      return {
        nbMen: jobQuote?.jobResolvedBranch.nbMen,
        nbTrucks: jobQuote?.jobResolvedBranch.nbTrucks,
      }
    }
    return {
      nbMen: utils.getQuoteNbMen(quote),
      nbTrucks: utils.getQuoteNbTrucks(quote),
    }
  }
}

/**
 * returns numbers of packing material products in a quote job
 */
export const useGetNbProductsInQuoteJob = () => {
  return () => (quoteJob: QuoteJob) => quoteJob.charges
    .filter(charge => charge.description === ChargeDescription.protectiveMaterialProduct)
    .reduce((acc, curr) => curr.quantity + acc, 0)
}

/**
 * return constraints of all jobs of a quote
 * if a jobIndex is provided, we returns only constraints of this job
 */
export const useGetQuoteConstraints = () => {
  return (quote: Quote, jobIndex?: number) => {
    return Array.from(new Set(quote.jobs
      .filter((_, index) => jobIndex === undefined || jobIndex === index)
      .map(job => job.constraints)
      .filter(c => !!c)
      .flat(),
    ))
  }
}

/*
 * the "item" value is based on the pricing description
 */
export const useQuoteItemValueFormatter = () => {
  const { t } = useTranslation()

  return (description: ChargeDescription, value?: string) => {
    switch (description) {
      case ChargeDescription.movingLabour:
      case ChargeDescription.packingLabour:
      case ChargeDescription.travelLabour:
      case ChargeDescription.transportLabour:
      case ChargeDescription.additionalChargesStairs:
      {
        const nbMen = parseInt(value ?? '0')
        if (nbMen === 0) {
          return
        }
        return t('quotes.pricing.item.man', { count: nbMen })
      }
      case ChargeDescription.travelTruckFeePerKilometer:
      case ChargeDescription.transportTruckFeePerKilometer:
      case ChargeDescription.additionalChargesOvernight:
      {
        const nbTrucks = parseInt(value ?? '0')
        if (nbTrucks < 2) {
          return
        }
        return t('quotes.pricing.item.truck', { count: nbTrucks })
      }
      case ChargeDescription.fixedTruckFee:
      {
        if (!value) {
          return
        }
        return t(`trucks.${value}.title`)
      }
      case ChargeDescription.additionalChargesSpecialServices:
      {
        if (!value) {
          return
        }
        return t(`specialServices.${value}.title`)
      }
      case ChargeDescription.additionalChargesSpecialItems:
      {
        if (!value) {
          return
        }
        return t(`specialItems.${value}.title`)
      }
      case ChargeDescription.protectiveMaterialProduct:
      {
        if (!value) {
          return
        }
        return t(`protectiveMaterial.${value}.title`)
      }
      default:
        return value
    }
  }
}

/*
 * return true if we need to use a description on the quote details line
 * with:      Moving Labour (4 men)
 * without:   Mattress bag
 */
export const useGetShowDescription = () => {
  return (description: ChargeDescription) => {
    const noDescriptionNeeded = [
      ChargeDescription.protectiveMaterialProduct,
      ChargeDescription.customCharge,
      ChargeDescription.customChargeMw,
    ]
    return !noDescriptionNeeded.includes(description)
  }
}

/*
 * return true if we need to use a description on the quote details line
 * with:      Moving Labour (4 men)
 * without:   Mattress bag
 */
export const useShowDescription = (description: ChargeDescription) => {
  return useGetShowDescription()(description)
}
